import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { Select, Spin } from "antd";
import moment from "moment";
import MySegmented from "../../components/segmented/Segmented";
import SkeletonTable from "../../components/table/SkeletonTable";
import columns from "../../columns/files/viewFile";
import viewFileNote from "../../columns/files/viewFileNote";
import View from "../../assets/View";
import Spinner from "../../components/loader/Spinner";
import Delete from "../../assets/Delete";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import {
  AssignTaskApi,
  DeleteServiceApi,
  GetEmployeeList,
  GetFileNoteApi,
  GetServiceTaskApi,
  GetTaskWiseFileApi,
} from "../../api/request/service";
import BackButton from "../../components/button/BackButton";
import UserAvatar from "../../components/common/UserAvatar";
import Search from "../../components/common/Search";
import GlobalTooltip from "../../components/common/GlobalTooltip";
import Edit from "../../assets/Edit";
import FileNotesModal from "./components/FileNotesModal";
import ModalComponent from "../../components/modals/ModalComponent";
import { tableScrollTop } from "../../utils/utils";
import Templates from "../../assets/Templates";
import AuditModal from "../reports/components/AuditModal";
import Album from "../../assets/Album";

const ViewFile = () => {
  const { fileTypeId } = useParams();
  const navigate = useNavigate();
  const userDetails = useSelector((state) => state?.user?.userDetails);
  const { state } = useLocation();
  const [service, setService] = useState("");
  const [taskList, setTaskList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [segmentOptions, setSegmentOptions] = useState([]);
  const [segmentedValue, setSegmentedValue] = useState(state?.task || "");
  const [fileList, setFileList] = useState([]);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [employeeList, setEmployeeList] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFile, setSelectedFile] = useState({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isFileNoteModalOpen, setIsFileNoteModalOpen] = useState(false);
  const [isFileNoteEditModal, setIsFileNoteEditModal] = useState(false);
  const [fileNoteList, setFileNoteList] = useState([]);
  const [isFileNoteLoading, setIsFileNoteLoading] = useState(false);
  const [isFileAuditModalOpen, setIsFileAuditModalOpen] = useState(false);

  const getServiceTaskList = async () => {
    try {
      setIsLoading(true);
      const params = { service_type_id: fileTypeId };
      const { data } = await GetServiceTaskApi(params);
      setService(data?.service_name);
      if (data?.status && data?.data?.length) {
        setTaskList(data?.data);
        const options = data?.data?.map((item) => {
          return {
            label: `${item.name} (${item?.total_file_count})`,
            value: item?.name,
          };
        });
        setSegmentOptions(options);
        if (!segmentedValue) setSegmentedValue(options[0]?.value);
      }
      setIsLoading(false);
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
      setIsLoading(false);
    }
  };

  const getEmployeeData = () => {
    const task = taskList?.find((task) => task?.name === segmentedValue);
    const employeeData = employeeList
      .filter((item) =>
        item?.permissions?.some((item) => item?.id === task?.id)
      )
      .map((item) => {
        return { label: item?.name, value: item?.id };
      });
    return employeeData;
  };

  const handleView = (data, fileType, clickBy = "button") => {
    const { action, assignTo, file, fileNotes, userDetails, ...rest } = data;
    const task = taskList?.find((task) => task?.name === segmentedValue);
    const safeTask = task
      ? {
          id: task?.id,
          name: task?.name,
          is_signature: task?.is_signature,
          is_send_review_docs: task?.is_send_review_docs,
        }
      : null;
    if (
      fileType === "View" &&
      (clickBy === "button" || !data?.is_bookkeeping)
    ) {
      navigate(`/file/${fileTypeId}/${data?.id}`, {
        state: { client: data?.user, task: safeTask },
      });
    } else {
      navigate(`/file/book_keeping/${fileTypeId}/${data?.id}`, {
        state: { client: clickBy === "row" ? rest : data },
      });
    }
  };

  const getFileList = async (searchText) => {
    try {
      setIsFileLoading(true);
      const task = taskList?.find((item) => item?.name === segmentedValue);
      const params = {
        task_id: task?.id,
        service_type_id: fileTypeId,
        page: currentPage,
        search: searchText,
      };
      const { data } = await GetTaskWiseFileApi(params);
      const employeeData = getEmployeeData();
      if (data?.status) {
        setFileList(
          data?.data?.map((item) => {
            const assignTo = employeeData?.find(
              (emp) => emp?.value === item?.employee_id
            );
            return {
              ...item,
              userDetails: <UserAvatar data={item?.user} />,
              file: (
                <div className="font-bold highlight-text capitalize">
                  {item?.service_name_slug}
                </div>
              ),
              submit: item?.is_submit ? "Yes" : "No",
              assignTo: (
                <div onClick={(e) => e.stopPropagation()}>
                  <Select
                    placeholder="Select employee"
                    style={{ width: "100%" }}
                    value={assignTo?.value || item?.employee_name}
                    options={employeeData}
                    onChange={(value) =>
                      assignHandler(item?.id, value, item?.employee_id)
                    }
                  />
                </div>
              ),
              startDate: item?.created_at
                ? moment(item?.created_at).format("lll")
                : "",
              fileNotes: (
                <div className="flex items-center gap-3">
                  <GlobalTooltip title={"View File Note"}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsFileNoteModalOpen(true);
                        getFileNotes(item?.id);
                        setSelectedFile(item);
                      }}
                    >
                      <View />
                    </button>
                  </GlobalTooltip>

                  <GlobalTooltip title={"Edit File Note"}>
                    <div
                      className="w-5 flex justify-center"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsFileNoteEditModal(true);
                        getFileNotes(item?.id);
                        setSelectedFile(item);
                      }}
                    >
                      <Edit />
                    </div>
                  </GlobalTooltip>
                </div>
              ),
              action: (
                <div
                  className="flex items-center gap-3"
                  onClick={(e) => e.stopPropagation()}
                >
                  <GlobalTooltip title={"View File"}>
                    <button onClick={() => handleView(item, "View")}>
                      <View />
                    </button>
                  </GlobalTooltip>
                  {item?.is_bookkeeping ? (
                    <GlobalTooltip title={"View BookKeeping File Submit"}>
                      <button onClick={() => handleView(item, "BookKeeping")}>
                        <Album />
                      </button>
                    </GlobalTooltip>
                  ) : null}
                  <GlobalTooltip title={"File Audit"}>
                    <button
                      onClick={() => {
                        setSelectedFile(item);
                        setIsFileAuditModalOpen(true);
                      }}
                    >
                      <Templates />
                    </button>
                  </GlobalTooltip>
                  {userDetails?.role === "admin" && (
                    <GlobalTooltip title={"Archive File"}>
                      <button
                        onClick={() => {
                          setSelectedFile(item);
                          setIsDeleteModalOpen(true);
                        }}
                      >
                        <Delete />
                      </button>
                    </GlobalTooltip>
                  )}
                </div>
              ),
            };
          })
        );
        setTotal(data?.total_files);
        setTotalRecords(data?.total_records);
      }
      setIsFileLoading(false);
    } catch (error) {
      setFileList([]);
      setIsFileLoading(false);
    }
  };

  const getFileNotes = async (id) => {
    try {
      setIsFileNoteLoading(true);
      const params = {
        file_id: id,
      };
      const { data } = await GetFileNoteApi(params);
      if (data?.status) {
        setFileNoteList(
          data?.data?.map((item) => {
            return {
              ...item,
              userDetails: <UserAvatar data={item?.user} />,
              fileNotes: <div className="w-52 text-wrap">{item?.notes}</div>,
            };
          })
        );
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsFileNoteLoading(false);
    }
  };

  const assignHandler = async (id, employee_id, currentEmployeeId) => {
    if (employee_id === currentEmployeeId) return;
    try {
      setFileList((prev) => {
        return prev.map((item) =>
          item.id === id ? { ...item, assignTo: <Spinner /> } : item
        );
      });

      let params = { file_id: id, employee_id: employee_id };
      const { data } = await AssignTaskApi(params);
      if (data?.status) setAssignData(id, employee_id);
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
      setAssignData(id, currentEmployeeId);
    }
  };

  const getEmployeeList = async () => {
    try {
      setIsLoading(true);
      const { data } = await GetEmployeeList();
      if (data.status) {
        setEmployeeList(data?.data);
        columns?.map((item) => {
          if (item.dataIndex === "assignTo") {
            item.filters = data?.data?.map((item) => {
              return { text: item?.name, value: item?.id };
            });
          }
        });
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getEmployeeList();
  }, []);

  useEffect(() => {
    if (fileTypeId) getServiceTaskList();
  }, [fileTypeId]);

  useEffect(() => {
    if (taskList?.length) getFileList();
  }, [segmentedValue, taskList, employeeList, currentPage]);

  useEffect(() => {
    tableScrollTop();
  }, [isFileNoteModalOpen]);

  const setAssignData = (id, employeeId) => {
    const employeeData = getEmployeeData();
    setFileList((prev) => {
      return prev.map((item) =>
        item.id === id
          ? {
              ...item,
              employee_id: employeeId,
              assignTo: (
                <div onClick={(e) => e.stopPropagation()}>
                  <Select
                    placeholder="Select employee"
                    style={{ width: "100%" }}
                    value={employeeId}
                    options={employeeData}
                    onChange={(value) =>
                      assignHandler(item?.id, value, employeeId)
                    }
                  />
                </div>
              ),
            }
          : item
      );
    });
  };

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination?.current);
  };

  const deleteFile = async () => {
    if (!selectedFile?.id) return;
    try {
      setIsDeleteLoading(true);
      const { data } = await DeleteServiceApi({ file_id: selectedFile?.id });
      if (data?.status) {
        toast.success(data?.message);
        setIsDeleteModalOpen(false);
        setSelectedFile({});
        getServiceTaskList();
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const updatedColumns = [...columns];
  updatedColumns?.splice(4, 0, {
    title: "Assigned To",
    dataIndex: "assignTo",
    render: (value) => (value ? value : "--"),
    width: 200,
    onFilter: (value, record) => {
      return record?.employee_id === value;
    },
  });

  return (
    <>
      <div className="border rounded-md bg-white">
        <div className="p-5">
          <div className="flex items-center gap-3">
            <BackButton />
            <h2 className="title capitalize">
              {service} {totalRecords ? `(${totalRecords})` : null}
            </h2>
          </div>
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center gap-2 my-16">
            <Spin />
            <p className="text-secondaryLight1">Loading...</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {taskList?.length ? (
              <>
                <div className="md:flex items-center md:justify-between px-3">
                  <div className="overflow-x-auto">
                    <MySegmented
                      options={segmentOptions}
                      value={segmentedValue}
                      setValue={setSegmentedValue}
                    />
                  </div>
                  <div className="w-48 ml-auto md:mt-0 mt-4">
                    <Search handleSearch={getFileList} />
                  </div>
                </div>

                <div className="table-radius-0 text-light mb-4">
                  <SkeletonTable
                    columns={updatedColumns}
                    data={fileList}
                    isLoading={isFileLoading}
                    pagination={{
                      current: currentPage,
                      total: total,
                      pageSize: 10,
                      showSizeChanger: false,
                    }}
                    handleTableChange={handleTableChange}
                    onRowClick={(data) => handleView(data, "View", "row")}
                  />
                </div>
              </>
            ) : (
              <p className="text-center text-Gray my-16">No task available</p>
            )}
          </div>
        )}
      </div>

      {/* --- Delete confirmation modal --- */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        message={
          <div>
            <p>
              Are you sure you want to archive this file?
            </p>
            <p className="text-lg text-gray-500 mt-1">
              If it remains archived, the file will be permanently deleted after
              60 days.
            </p>
          </div>
        }
        onConfirm={deleteFile}
        isLoading={isDeleteLoading}
        loadingLabel={`Deleting file...`}
      />

      {/* show all file notes modal*/}
      <ModalComponent
        isOpen={isFileNoteModalOpen}
        setIsOpen={setIsFileNoteModalOpen}
        title={
          <div className="text-center">
            <p>File Notes</p>
            <p className="capitalize">{`${selectedFile?.service_name_slug} - (#${selectedFile?.file_number})`}</p>
          </div>
        }
        width={1200}
      >
        <SkeletonTable
          columns={viewFileNote}
          data={fileNoteList}
          isLoading={isFileNoteLoading}
          stickHeader={true}
        />
      </ModalComponent>

      {/* edit file notes modal*/}
      <FileNotesModal
        isOpen={isFileNoteEditModal}
        setIsOpen={setIsFileNoteEditModal}
        selectedFile={selectedFile}
        task={taskList?.find((task) => task?.name === segmentedValue)}
      />

      {/* view file audit report */}
      <AuditModal
        file={selectedFile}
        isOpen={isFileAuditModalOpen}
        setIsOpen={setIsFileAuditModalOpen}
      />
    </>
  );
};

export default ViewFile;
